import { Link, useNavigate, useParams } from "react-router-dom"
import DashboardSideBar from "../../components/dashboardSidebar"
import Footer from "../../components/footer"
import Header from "../../components/header"
import SideBar from "../../components/sidebar"
import { useState, useEffect, useRef } from "react"
import useIsMobile from "../../hooks/useIsMobile";
import MobileHeader from "../../components/MobileHeader";
import services from "../../services"
import Toaster from "../../components/toaster";
import moment from 'moment'
import CitySelect from "../../components/CitySelect";

const EditOneWayAvailability = () => {
    let navigate = useNavigate();
    const isMobileView = useIsMobile();
    const { id } = useParams();
    const [availabilityData, setAvailabilityData] = useState({
        vendorCarsId: '',
        from: {
            longitude: '',
            latitude: '',
            full_address: ''
        },
        to: {
            longitude: '',
            latitude: '',
            full_address: ''
        },
        tripDate: '',
        contactNumber: '',
        alternativeContactNumber: '',
        isActive: true
    })
    const [toaster, setToaster] = useState({ visiblity: "hide" });
    const [loading, setLoading] = useState(false)
    const [fetching, setFetching] = useState(true)
    const [carsList, setCarsList] = useState([])
    const [carsLoading, setCarsLoading] = useState(true)
    // Trips are dated only (no time); can't be in the past
    
    // Location search states
    const [fromSearchTerm, setFromSearchTerm] = useState('')
    const [toSearchTerm, setToSearchTerm] = useState('')
    const [fromSuggestions, setFromSuggestions] = useState([])
    const [toSuggestions, setToSuggestions] = useState([])
    const [showFromSuggestions, setShowFromSuggestions] = useState(false)
    const [citiesList, setCitiesList] = useState([])
    const [showToSuggestions, setShowToSuggestions] = useState(false)
    const fromSearchTimeoutRef = useRef(null)
    const toSearchTimeoutRef = useRef(null)
    const fromDropdownRef = useRef(null)
    const toDropdownRef = useRef(null)

    // Fetch vendor cars and subscriptions
    useEffect(() => {
        services.Cars.GET_CITIES().then((r) => setCitiesList(r.data || [])).catch(() => {});
        const fetchCars = async () => {
            setCarsLoading(true)
            try {
                const response = await services.Cars.GET_VENDOR_CAR();
                setCarsList(response.data || [])
            } catch (error) {
                console.log(error)
            } finally {
                setCarsLoading(false)
            }
        }

        fetchCars()
    }, [])

    // Fetch availability details
    const getAvailabilityDetails = async () => {
        setFetching(true)
        try {
            const response = await services.Vendor.GET_ONE_WAY_AVAILABILITY_BY_ID(id);
            const data = response.data;
            // Completed trips are locked — send the vendor back to the details page
            if (data.isCompleted) {
                navigate(`/oneWayAvailabilities/${id}`, { replace: true });
                return;
            }
            setAvailabilityData({
                vendorCarsId: data.vendorCarsId?.toString() || '',
                from: data.from || {
                    longitude: '',
                    latitude: '',
                    full_address: ''
                },
                to: data.to || {
                    longitude: '',
                    latitude: '',
                    full_address: ''
                },
                tripDate: moment(data.tripDate).format('YYYY-MM-DD'),
                contactNumber: data.contactNumber || '',
                alternativeContactNumber: data.alternativeContactNumber || '',
                isActive: data.isActive !== undefined ? data.isActive : true
            })
            setFromSearchTerm(data.from?.full_address || '')
            setToSearchTerm(data.to?.full_address || '')
        } catch (error) {
            console.log(error)
            setToaster({
                type: "danger",
                title: "Error Occured",
                text: error.response?.data?.message || "Failed to fetch availability details",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
        } finally {
            setFetching(false)
        }
    }

    useEffect(() => {
        if (id) {
            getAvailabilityDetails()
        }
    }, [id])

    // Handle from location search
    const handleFromSearch = async (searchKey) => {
        if (!searchKey || searchKey.length < 2) {
            setFromSuggestions([])
            setShowFromSuggestions(false)
            return
        }

        try {
            const response = await services.Location.GET_LOCATION(searchKey);
            if (response.data?.features) {
                setFromSuggestions(response.data.features)
                setShowFromSuggestions(true)
            }
        } catch (error) {
            console.log(error)
            setFromSuggestions([])
            setShowFromSuggestions(false)
        }
    }

    // Handle to location search
    const handleToSearch = async (searchKey) => {
        if (!searchKey || searchKey.length < 2) {
            setToSuggestions([])
            setShowToSuggestions(false)
            return
        }

        try {
            const response = await services.Location.GET_LOCATION(searchKey);
            if (response.data?.features) {
                setToSuggestions(response.data.features)
                setShowToSuggestions(true)
            }
        } catch (error) {
            console.log(error)
            setToSuggestions([])
            setShowToSuggestions(false)
        }
    }

    // Handle from input change with debounce
    const handleFromInputChange = (value) => {
        setFromSearchTerm(value)
        if (fromSearchTimeoutRef.current) {
            clearTimeout(fromSearchTimeoutRef.current)
        }
        fromSearchTimeoutRef.current = setTimeout(() => {
            handleFromSearch(value)
        }, 300)
    }

    // Handle to input change with debounce
    const handleToInputChange = (value) => {
        setToSearchTerm(value)
        if (toSearchTimeoutRef.current) {
            clearTimeout(toSearchTimeoutRef.current)
        }
        toSearchTimeoutRef.current = setTimeout(() => {
            handleToSearch(value)
        }, 300)
    }

    const handleCityFrom = (cid) => {
        const c = (citiesList || []).find((x) => String(x.id) === String(cid));
        setAvailabilityData((prev) => ({
            ...prev,
            from: c
                ? { longitude: c.longitude, latitude: c.latitude, full_address: c.name, cityId: c.id }
                : { longitude: '', latitude: '', full_address: '' }
        }));
    };
    // Handle from location selection
    const handleFromSelect = (feature) => {
        setAvailabilityData(prev => ({
            ...prev,
            from: {
                longitude: feature.properties.coordinates.longitude,
                latitude: feature.properties.coordinates.latitude,
                full_address: feature.properties.full_address
            }
        }))
        setFromSearchTerm(feature.properties.full_address)
        setShowFromSuggestions(false)
    }

    // Handle to location selection
    const handleToSelect = (feature) => {
        setAvailabilityData(prev => ({
            ...prev,
            to: {
                longitude: feature.properties.coordinates.longitude,
                latitude: feature.properties.coordinates.latitude,
                full_address: feature.properties.full_address
            }
        }))
        setToSearchTerm(feature.properties.full_address)
        setShowToSuggestions(false)
    }

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target)) {
                setShowFromSuggestions(false)
            }
            if (toDropdownRef.current && !toDropdownRef.current.contains(event.target)) {
                setShowToSuggestions(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const HandleOnChangeValue = (value, key) => {
        setAvailabilityData({
            ...availabilityData,
            [key]: value
        })
    }

    const onSubmitHandler = async () => {
        // Build payload with only changed fields
        const payload = {}
        
        if (availabilityData.vendorCarsId) {
            payload.vendorCarsId = parseInt(availabilityData.vendorCarsId)
        }
        if (availabilityData.from.longitude && availabilityData.from.latitude && availabilityData.from.full_address) {
            payload.from = availabilityData.from
        }
        if (availabilityData.to.longitude && availabilityData.to.latitude && availabilityData.to.full_address) {
            payload.to = availabilityData.to
        }
        if (availabilityData.contactNumber) {
            payload.contactNumber = availabilityData.contactNumber
        }
        if (availabilityData.alternativeContactNumber !== undefined) {
            payload.alternativeContactNumber = availabilityData.alternativeContactNumber || null
        }
        if (availabilityData.isActive !== undefined) {
            payload.isActive = availabilityData.isActive
        }

        // Check if at least one field is provided
        if (Object.keys(payload).length === 0) {
            setToaster({
                type: "danger",
                title: "Validation Error",
                text: "At least one field must be updated",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
            return;
        }

        setLoading(true)
        try {
            const response = await services.Vendor.UPDATE_ONE_WAY_AVAILABILITY(id, payload);
            console.log(response)
            setToaster({
                type: "success",
                title: "Availability Updated",
                text: 'Availability updated successfully',
                visiblity: "show",
            });
            setTimeout(() => {
                navigate('/oneWayAvailabilities')
            }, 300)
        } catch (error) {
            console.log(error)
            setToaster({
                type: "danger",
                title: "Error Occured",
                text: error.response?.data?.message || "Failed to update availability",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({
                    visiblity: "hide",
                });
            }, 1500)
        } finally {
            setLoading(false)
        }
    }

    if (isMobileView) {
        if (fetching) {
            return (<main className="mlux" style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}><div className="mlux-empty"><div className="ic"><i className="far fa-spinner"></i></div><h4>Loading…</h4></div></main>);
        }
        return (
            <>
                <Toaster type={toaster.type} title={toaster.title} text={toaster.text} visiblity={toaster.visiblity} />
                <SideBar />
                <main className="mlux mlux-auth">
                    <MobileHeader back={`/oneWayAvailabilities/${id}`} />
                    <div className="mlux-greet" style={{ margin: '8px 4px 2px' }} data-rise="1">
                        <div className="kick">Update details</div>
                        <div className="nm" style={{ fontSize: 22 }}>Edit Availability</div>
                    </div>
                    <div className="mlux-card" style={{ padding: 16, marginTop: 12 }} data-rise="2">
                        <label className="mlux-lbl">Select car</label>
                        <div className="mlux-field no-icon select"><select value={availabilityData.vendorCarsId || ''} onChange={(e) => HandleOnChangeValue(e.target.value, 'vendorCarsId')}><option value="">Select car</option>{carsList.map((car) => <option key={car.id} value={car.id}>{car.Car?.name || car.car?.name} ({car.Car?.brand || car.car?.brand}) - {car.rcNumber}</option>)}</select></div>

                        <label className="mlux-lbl">Trip date</label>
                        <div className="mlux-field"><i className="far fa-calendar"></i><input type="date" value={availabilityData.tripDate || ''} disabled /></div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', margin: '-4px 2px 10px' }}>Trip date can't be changed. Create a new availability and delete this one instead.</div>

                        <label className="mlux-lbl">From city</label>
                        <CitySelect variant="mobile" icon="far fa-location-dot" cities={citiesList} value={availabilityData.from?.cityId} onChange={handleCityFrom} />

                        <label className="mlux-lbl">To location</label>
                        <div className="mlux-field"><i className="fas fa-flag-checkered"></i><input type="text" value={toSearchTerm} placeholder="Search to…" onChange={(e) => handleToInputChange(e.target.value)} /></div>
                        {showToSuggestions && toSuggestions.length > 0 && <ul className="mlux-suggest">{toSuggestions.map((f, i) => <li key={i} onClick={() => handleToSelect(f)}><i className="far fa-location-dot"></i>{f.properties.full_address}</li>)}</ul>}

                        <label className="mlux-lbl">Contact number</label>
                        <div className="mlux-field no-icon"><input type="number" value={availabilityData.contactNumber} placeholder="Phone" onChange={(e) => HandleOnChangeValue(e.target.value, 'contactNumber')} /></div>
                        <label className="mlux-lbl">Alternative contact (optional)</label>
                        <div className="mlux-field no-icon"><input type="number" value={availabilityData.alternativeContactNumber} placeholder="Alternative phone" onChange={(e) => HandleOnChangeValue(e.target.value, 'alternativeContactNumber')} /></div>

                        <button className="mlux-btn" style={{ marginTop: 12 }} disabled={loading} onClick={onSubmitHandler}><i className="far fa-circle-check"></i> {loading ? 'Saving…' : 'Save changes'}</button>
                    </div>
                </main>
            </>
        );
    }

    if (fetching) {
        return (<>
            <Header />
            <SideBar />
            <main class="main">
                <div class="user-profile py-5">
                    <div class="container">
                        <div class="row">
                            <DashboardSideBar />
                            <div class="col-lg-9">
                                <div class="text-center py-5">
                                    <p>Loading availability details...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </>)
    }

    return (<>
        <Header />
        <SideBar />
        <main class="main">

            <Toaster
                type={toaster.type}
                title={toaster.title}
                text={toaster.text}
                visiblity={toaster.visiblity}
            />

            <div class="user-profile py-5">
                <div class="container">
                    <div class="row">
                        <DashboardSideBar />
                        <div class="col-lg-9">
                            <div class="user-profile-wrapper">
                                <div class="row">
                                    <div class="col-lg-12">
                                        <div class="user-profile-card">
                                            <div class="user-profile-card-header">
                                                <h4 class="user-profile-card-title">Edit One-Way Availability</h4>
                                            </div>
                                            <div class="user-profile-form">

                                                <div class="row">
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Car</label>
                                                            <select
                                                                onChange={(e) => HandleOnChangeValue(e.target.value, 'vendorCarsId')}
                                                                class="form-control"
                                                                value={availabilityData.vendorCarsId}
                                                                disabled={carsLoading}
                                                            >
                                                                <option value="">Select Car</option>
                                                                {carsList.map((car) => (
                                                                    <option key={car.id} value={car.id}>
                                                                        {car.Car?.name || car.car?.name || 'N/A'} ({car.Car?.brand || car.car?.brand || 'N/A'}) - {car.rcNumber || 'N/A'}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Trip Date</label>
                                                            <input
                                                                type="date"
                                                                class="form-control"
                                                                value={availabilityData.tripDate}
                                                                placeholder="Trip Date"
                                                                disabled
                                                            />
                                                            <small className="text-muted" style={{ display: 'block', marginTop: '5px' }}>
                                                                Trip date can't be changed. Create a new availability and delete this one instead.
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>From City</label>
                                                            <CitySelect cities={citiesList} value={availabilityData.from?.cityId} onChange={handleCityFrom} />
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group" ref={toDropdownRef} style={{ position: 'relative' }}>
                                                            <label>To Location</label>
                                                            <input
                                                                type="text"
                                                                onChange={(e) => handleToInputChange(e.target.value)}
                                                                class="form-control"
                                                                value={toSearchTerm}
                                                                placeholder="Search to location..."
                                                            />
                                                            {showToSuggestions && toSuggestions.length > 0 && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: 0,
                                                                    right: 0,
                                                                    backgroundColor: 'white',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px',
                                                                    maxHeight: '200px',
                                                                    overflowY: 'auto',
                                                                    zIndex: 1000,
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                                }}>
                                                                    {toSuggestions.map((feature, index) => (
                                                                        <div
                                                                            key={index}
                                                                            onClick={() => handleToSelect(feature)}
                                                                            style={{
                                                                                padding: '10px',
                                                                                cursor: 'pointer',
                                                                                borderBottom: '1px solid #eee'
                                                                            }}
                                                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                                                        >
                                                                            {feature.properties.full_address}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Contact Number</label>
                                                            <input
                                                                type="tel"
                                                                onChange={(e) => HandleOnChangeValue(e.target.value, 'contactNumber')}
                                                                class="form-control"
                                                                value={availabilityData.contactNumber}
                                                                placeholder="Contact Number"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Alternative Contact Number</label>
                                                            <input
                                                                type="tel"
                                                                onChange={(e) => HandleOnChangeValue(e.target.value, 'alternativeContactNumber')}
                                                                class="form-control"
                                                                value={availabilityData.alternativeContactNumber}
                                                                placeholder="Alternative Contact Number (Optional)"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Status</label>
                                                            <select
                                                                onChange={(e) => HandleOnChangeValue(e.target.value === 'true', 'isActive')}
                                                                class="form-control"
                                                                value={availabilityData.isActive ? 'true' : 'false'}
                                                            >
                                                                <option value="true">Active</option>
                                                                <option value="false">Inactive</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="d-flex align-items-center gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={onSubmitHandler}
                                                        class="theme-btn my-3"
                                                        disabled={loading}
                                                    >
                                                        <span class="far fa-save"></span> {loading ? 'Updating...' : 'Update Availability'}
                                                    </button>
                                                    <Link to={'/oneWayAvailabilities'} class="theme-btn my-3" style={{ background: '#6c757d' }}>
                                                        <span class="far fa-times"></span> Cancel
                                                    </Link>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </main>
        <Footer />
    </>
    )
}
export default EditOneWayAvailability

